import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { CheckCircle2, Copy, EyeOff, MapPin, Megaphone, Printer, Shield } from 'lucide-react';
import ComplaintPhotoSlider from '../../components/Complaint/ComplaintPhotoSlider';
import { complaintService } from '../../services/api';
import { userFacingError } from '../../i18n/userFacingError';
import ConsultaPublicaSearch from '../../components/Public/ConsultaPublicaSearch';
import PublicDenunciaResult from '../../components/Public/PublicDenunciaResult';
import PublicFaq from '../../components/Public/PublicFaq';
import PublicPageShell from '../../components/Public/PublicPageShell';
import PublicChannelClosed from '../../components/Public/PublicChannelClosed';
import { useTenant } from '../../contexts/TenantContext';
import { isPublicConsultaEnabled, isPublicDenunciaEnabled } from '../../utils/publicPages';
import { UF_OPTIONS, copyText } from '../../utils/publicStatus';
import { printPublicReceipt } from '../../utils/publicReceipt';
import { receiptBrandFromTenant } from '../../utils/tenantBranding';

function unwrap(payload) {
  return Array.isArray(payload) ? payload : payload?.data ?? [];
}

const empty = {
  category_id: '',
  descricao: '',
  local: '',
  cidade: '',
  estado: '',
  bairro: '',
  rua: '',
  ponto_referencia: '',
};

const DenunciaPublica = () => {
  const { tenant } = useTenant();
  const [params, setParams] = useSearchParams();
  const initialProtocolo = params.get('protocolo') || '';
  const [tab, setTab] = useState(initialProtocolo ? 'consulta' : 'nova');
  const [form, setForm] = useState(empty);
  const [protocolo, setProtocolo] = useState(initialProtocolo);
  const [showLocal, setShowLocal] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [pendingPreviews, setPendingPreviews] = useState([]);

  useEffect(() => {
    const next = pendingFiles.map((file, idx) => ({
      id: `pending-${idx}-${file.name}`,
      src: URL.createObjectURL(file),
      file,
      alt: file.name,
    }));
    setPendingPreviews(next);
    return () => next.forEach((item) => URL.revokeObjectURL(item.src));
  }, [pendingFiles]);
  const orgName = tenant?.name;
  const channelOn = isPublicDenunciaEnabled(tenant);

  const { data: catRaw } = useQuery(
    ['public-complaint-categories'],
    () => complaintService.getPublicCategories().then((r) => r.data),
    { enabled: channelOn }
  );
  const categories = useMemo(() => unwrap(catRaw), [catRaw]);

  const send = useMutation(
    async () => {
      const res = await complaintService.createPublicDenuncia({
        ...form,
        data: new Date().toISOString(),
        type_send: 3,
        client_id: null,
      });
      const proto = res?.data?.protocolo || '';
      if (proto && pendingFiles.length) {
        for (const file of pendingFiles) {
          await complaintService.uploadPublicDenunciaFoto(proto, file);
        }
      }
      return res;
    },
    {
      onSuccess: (res) => {
        const proto = res?.data?.protocolo || res?.data?.id || '';
        setProtocolo(proto);
        setForm(empty);
        setPendingFiles([]);
        const next = new URLSearchParams(params);
        if (proto) next.set('protocolo', proto);
        setParams(next, { replace: true });
        toast.success(
          pendingFiles.length
            ? 'Denúncia e evidências registradas neste órgão.'
            : 'Denúncia registrada neste órgão.'
        );
      },
      onError: (e) => toast.error(userFacingError(e, 'saveError')),
    }
  );

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  if (!channelOn) {
    return (
      <PublicPageShell
        eyebrow="Participação cidadã"
        title="Denúncia ambiental anônima"
        lead="Este município desligou o canal público de denúncia. O registro interno pelo sistema autenticado continua disponível para a equipe."
      >
        <PublicChannelClosed
          title="Denúncia anônima indisponível"
          text="O administrador deste órgão desativou o envio e o acompanhamento sem cadastro. Entre no sistema se você faz parte da equipe."
        />
      </PublicPageShell>
    );
  }

  return (
    <PublicPageShell
      eyebrow="Participação cidadã"
      title="Denúncia ambiental anônima"
      lead="Envie um relato a este órgão sem cadastro e sem login. O sistema não identifica quem denuncia — guarde o protocolo para acompanhar."
      aside={
        <>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 text-sm text-slate-200">
            <p className="flex items-center gap-2 font-semibold text-white">
              <EyeOff className="h-4 w-4 text-primary-300" aria-hidden />
              Anonimato de verdade
            </p>
            <p className="mt-2 leading-relaxed text-slate-300">
              Não pedimos nome, e-mail nem documento. O registro entra como denúncia anônima e o andamento público só mostra situação e município.
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 text-sm text-slate-300">
            <p className="flex items-center gap-2 font-semibold text-white">
              <Shield className="h-4 w-4 text-primary-300" aria-hidden />
              Depois do envio
            </p>
            <ol className="mt-3 list-decimal space-y-1.5 pl-4">
              <li>Você recebe um protocolo único.</li>
              <li>A equipe faz a triagem e, se couber, a vistoria.</li>
              <li>A situação muda nesta mesma página, pelo número.</li>
            </ol>
          </div>
          {isPublicConsultaEnabled(tenant) ? (
            <Link
              to="/process-lookup"
              className="block rounded-2xl border border-white/10 bg-gradient-to-br from-primary-800/40 to-slate-900/40 p-5 text-sm text-slate-200 hover:border-white/20"
            >
              <p className="font-semibold text-white">Tem um processo, não uma denúncia?</p>
              <p className="mt-1 text-slate-300">Consulte o andamento pelo número do licenciamento.</p>
              <p className="mt-2 font-semibold text-primary-200">Ir para consulta de processo →</p>
            </Link>
          ) : null}
          <PublicFaq
            items={[
              {
                q: 'Alguém vai saber quem eu sou?',
                a: 'Não. Esta página não pede identificação e a consulta pública não revela o texto do relato nem dados pessoais.',
              },
              {
                q: 'E se eu perder o protocolo?',
                a: 'Não reenviamos por e-mail. Imprima ou copie o número agora. Sem ele, o órgão não consegue localizar o registro anônimo.',
              },
              {
                q: 'Quando muda a situação?',
                a: 'Depois da triagem, vistoria ou encaminhamento interno. Volte com o mesmo número — o link desta página já pode levar o protocolo.',
              },
            ]}
          />
        </>
      }
    >
      <div className="mb-6 flex rounded-xl bg-slate-100 p-1 text-sm font-semibold">
        <button
          type="button"
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 ${
            tab === 'nova' ? 'bg-white text-primary-900 shadow-sm' : 'text-slate-600'
          }`}
          onClick={() => setTab('nova')}
        >
          <Megaphone className="h-4 w-4" aria-hidden />
          Nova denúncia
        </button>
        <button
          type="button"
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 ${
            tab === 'consulta' ? 'bg-white text-primary-900 shadow-sm' : 'text-slate-600'
          }`}
          onClick={() => setTab('consulta')}
        >
          Acompanhar protocolo
        </button>
      </div>

      {tab === 'nova' ? (
        <>
          {protocolo ? (
            <div className="mb-6 overflow-hidden rounded-2xl border border-emerald-200 bg-emerald-50">
              <div className="flex items-start gap-3 px-4 py-4">
                <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-emerald-700" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-emerald-950">Denúncia registrada neste órgão</p>
                  <p className="mt-1 text-sm text-emerald-900/80">
                    Anote o protocolo. Sem ele não é possível consultar o andamento — e não reenviamos por e-mail.
                  </p>
                  <p className="mt-3 font-mono text-xl font-bold tracking-wide text-emerald-950">{protocolo}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={async () => {
                        const ok = await copyText(protocolo);
                        toast[ok ? 'success' : 'error'](ok ? 'Protocolo copiado.' : 'Não foi possível copiar.');
                      }}
                    >
                      <Copy className="mr-1.5 h-4 w-4" aria-hidden />
                      Copiar protocolo
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => {
                        const ok = printPublicReceipt({
                          ...receiptBrandFromTenant(tenant, orgName),
                          title: 'Protocolo de denúncia anônima',
                          protocol: protocolo,
                          fields: [['Situação', 'Recebida neste órgão']],
                          note: 'Guarde este comprovante. O órgão não reenvia o número por e-mail.',
                        });
                        if (!ok) toast.error('Permita pop-ups para imprimir o comprovante.');
                      }}
                    >
                      <Printer className="mr-1.5 h-4 w-4" aria-hidden />
                      Imprimir comprovante
                    </button>
                    <button type="button" className="btn btn-outline" onClick={() => setTab('consulta')}>
                      Consultar andamento
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (!form.category_id || !form.descricao.trim()) {
                toast.error('Informe a categoria e a descrição.');
                return;
              }
              send.mutate();
            }}
          >
            <div>
              <label className="form-label" htmlFor="denuncia-categoria">
                Categoria
              </label>
              <select
                id="denuncia-categoria"
                className="form-select"
                value={form.category_id}
                onChange={setField('category_id')}
                required
              >
                <option value="">Selecione o tipo de ocorrência</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label" htmlFor="denuncia-descricao">
                O que aconteceu
              </label>
              <textarea
                id="denuncia-descricao"
                className="form-input"
                rows={5}
                value={form.descricao}
                onChange={setField('descricao')}
                placeholder="Descreva o fato, quando ocorreu e qualquer detalhe que ajude a localizar (sem se identificar)."
                required
              />
              <p className="mt-1 text-right text-xs text-gray-400">{form.descricao.trim().length} caracteres</p>
            </div>

            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-800"
              onClick={() => setShowLocal((v) => !v)}
            >
              <MapPin className="h-4 w-4" aria-hidden />
              {showLocal ? 'Ocultar localização' : 'Informar localização (opcional)'}
            </button>

            {showLocal ? (
              <div className="grid gap-3 rounded-xl bg-slate-50 p-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="form-label">Local ou ponto conhecido</label>
                  <input className="form-input" value={form.local} onChange={setField('local')} />
                </div>
                <div>
                  <label className="form-label">Cidade</label>
                  <input className="form-input" value={form.cidade} onChange={setField('cidade')} />
                </div>
                <div>
                  <label className="form-label">UF</label>
                  <select className="form-select" value={form.estado} onChange={setField('estado')}>
                    <option value="">—</option>
                    {UF_OPTIONS.map((uf) => (
                      <option key={uf} value={uf}>
                        {uf}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Bairro</label>
                  <input className="form-input" value={form.bairro} onChange={setField('bairro')} />
                </div>
                <div>
                  <label className="form-label">Rua / via</label>
                  <input className="form-input" value={form.rua} onChange={setField('rua')} />
                </div>
                <div className="sm:col-span-2">
                  <label className="form-label">Ponto de referência</label>
                  <input className="form-input" value={form.ponto_referencia} onChange={setField('ponto_referencia')} />
                </div>
              </div>
            ) : null}

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <ComplaintPhotoSlider
                title="Evidências Fotográficas"
                items={pendingPreviews}
                onAdd={(files) => setPendingFiles((prev) => [...prev, ...files])}
                onRemove={(item) => setPendingFiles((prev) => prev.filter((f) => f !== item.file))}
              />
              <p className="mt-2 text-xs text-slate-500">JPG, PNG ou WEBP (máx. 15 MB). Opcional.</p>
            </div>

            <button type="submit" className="btn btn-primary w-full py-3" disabled={send.isLoading}>
              {send.isLoading ? 'Enviando…' : 'Enviar denúncia anônima'}
            </button>
          </form>
        </>
      ) : (
        <ConsultaPublicaSearch
          title="Consultar denúncia pelo protocolo"
          description="Use o número recebido no envio (em geral no formato DEN-AAAA-XXXXXX)."
          placeholder="DEN-2026-XXXXXX"
          entityLabel="denúncia"
          minLength={4}
          initialQuery={protocolo}
          autoSearch={Boolean(protocolo)}
          idleHint="Informe o protocolo completo. A busca pública não revela o texto da denúncia."
          onSearched={(needle) => {
            const next = new URLSearchParams(params);
            if (needle) next.set('protocolo', needle);
            else next.delete('protocolo');
            setParams(next, { replace: true });
          }}
          search={async (numero) => {
            const { data } = await complaintService.consultarDenunciaPublica(numero);
            return data;
          }}
          renderItem={(row) => (
            <>
              <strong>{row.protocolo}</strong> · {row.status_label || row.status}
              {row.cidade ? ` · ${row.cidade}` : ''}
            </>
          )}
          renderResults={(items) => (
            <div className="space-y-4">
              {items.map((row, idx) => (
                <PublicDenunciaResult key={row.protocolo || idx} row={row} orgName={orgName} />
              ))}
            </div>
          )}
        />
      )}
    </PublicPageShell>
  );
};

export default DenunciaPublica;
